import React from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddressEditor from '../../../common/addressSchedule/AddressEditor.tsx';
import WeeklyScheduleEditor from '../../../common/addressSchedule/WeeklyScheduleEditor.tsx';
import LockedArea from './ConfigLockedArea';

export default function ConfiguracoesTabModals({
  modalOpen,
  savingConfig,
  handleCloseModal,
  handleSubmit,
  clubeModuleRuleLoading,
  clubeSharedFieldsLocked,
  principalAddressDetails,
  form,
  enderecoPrincipalQuery,
  buscandoEnderecoPrincipal,
  enderecoPrincipalResultados,
  setEnderecoPrincipalQuery,
  setEnderecoPrincipalResultados,
  handleBuscarEnderecoPrincipal,
  handleSelecionarEnderecoPrincipal,
  handleChange,
  handlePrincipalAddressFieldChange,
  clubeScheduleDays,
  handleBaseHorarioChange,
  handleAddHorarioExtra,
  updateClubeSchedulePeriod,
  removeClubeSchedulePeriod,
  replicateClubeScheduleToAll,
  localidadeDialogOpen,
  handleCloseLocalidadeDialog,
  localidadeForm,
  handleLocalidadeFieldChange,
  enderecoQuery,
  setEnderecoQuery,
  handleBuscarEndereco,
  buscandoEndereco,
  enderecoResultados,
  handleSelecionarEndereco,
  savingLocalidade,
  handleSaveLocalidade,
  snackbar,
  setSnackbar,
}) {
  return (
    <>
    <Dialog
      open={modalOpen}
      onClose={(_, reason) => {
        if (savingConfig && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
        handleCloseModal();
      }}
      disableEscapeKeyDown={savingConfig}
      maxWidth="md"
      fullWidth
      PaperProps={{ 'data-tour': 'config-modal-paper' }}
    >
      <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
        <SettingsIcon sx={{ verticalAlign: 'middle', mr: 1 }} color="primary" />
        Revisar configurações de check-in
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}  data-tour="config-modal-content">
        <LockedArea
          locked={clubeModuleRuleLoading}
          message="Aguarde o carregamento das configurações do Clube de Mesa."
        >
          <Box mb={3}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
            <EditLocationAltIcon color="primary" />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Área válida para check-in
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Define onde o cliente precisa estar para validar um check-in.
              </Typography>
            </Box>
          </Box>
            <AddressEditor
              values={{
                rua: principalAddressDetails.logradouro || '',
                numero: principalAddressDetails.numero || '',
                bairro: principalAddressDetails.bairro || '',
                cep: principalAddressDetails.cep || '',
                cidade: principalAddressDetails.cidade || '',
                estado: principalAddressDetails.estado || '',
                endereco_lat: String(form.lat ?? ''),
                endereco_long: String(form.long ?? ''),
              }}
              inputClassName="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              searchValue={enderecoPrincipalQuery}
              searching={buscandoEnderecoPrincipal}
              searchResults={enderecoPrincipalResultados}
              onSearchValueChange={(value) => {
                setEnderecoPrincipalQuery(value);
                setEnderecoPrincipalResultados([]);
              }}
              onSearch={handleBuscarEnderecoPrincipal}
              onSelectSearchResult={handleSelecionarEnderecoPrincipal}
              onFieldChange={(field, value) => {
                if (field === 'endereco_lat') handleChange('lat', value);
                else if (field === 'endereco_long') handleChange('long', value);
                else handlePrincipalAddressFieldChange(field === 'rua' ? 'logradouro' : field, value);
              }}
              searchLabel="Buscar endereço do Clube de Mesa"
              cityListId="clube-cidade-list"
            />
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Complemento (opcional)"
                  fullWidth
                  value={principalAddressDetails.complemento || ''}
                  disabled={clubeSharedFieldsLocked}
                  onChange={(e) => handlePrincipalAddressFieldChange('complemento', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Raio permitido para check-in (m)"
                  fullWidth
                  value={form.raio}
                  disabled={clubeSharedFieldsLocked}
                  onChange={(e) => handleChange('raio', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </LockedArea>

        <Divider sx={{ my: 3 }} />

        <LockedArea
          locked={clubeModuleRuleLoading}
          message="Aguarde o carregamento dos horários do Clube de Mesa."
        >
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
            <AccessTimeIcon color="primary" />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Quando o check-in é permitido
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fora desses horários, os check-ins ficam desativados automaticamente.
              </Typography>
            </Box>
          </Box>
          <WeeklyScheduleEditor
            days={clubeScheduleDays}
            inputClassName="rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            disabled={clubeSharedFieldsLocked || savingConfig}
            onToggleDayClosed={(dayIndex, closed) => handleBaseHorarioChange(dayIndex, 'fechado', closed)}
            onAddPeriod={(dayIndex) => handleAddHorarioExtra(dayIndex)}
            onUpdatePeriod={updateClubeSchedulePeriod}
            onRemovePeriod={removeClubeSchedulePeriod}
            onReplicateAll={replicateClubeScheduleToAll}
          />
        </Box>
        </LockedArea>

        <Divider sx={{ my: 3 }} />

        <Accordion
          defaultExpanded={false}
          sx={{
            borderRadius: 2,
            border: '1px solid #eee',
            boxShadow: 'none',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Configurações avançadas</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Recomendado apenas se você souber o que está fazendo.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Intervalo entre check-ins (h)"
                  fullWidth
                  value={form.intervalo_checkin}
                  onChange={(e) => handleChange('intervalo_checkin', e.target.value)}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCloseModal} color="inherit" disabled={savingConfig}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={savingConfig}
          className="clube-primary-button"
        >
          {savingConfig ? (
            <>
              <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              Salvando...
            </>
          ) : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={localidadeDialogOpen}
      onClose={handleCloseLocalidadeDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
        <EditLocationAltIcon sx={{ verticalAlign: 'middle', mr: 1 }} color="primary" />
        {localidadeForm.id ? 'Editar Localidade' : 'Nova Localidade'}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nome"
              fullWidth
              value={localidadeForm.nome}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('nome', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              label="Buscar endereço"
              fullWidth
              value={enderecoQuery}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => setEnderecoQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleBuscarEndereco();
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} display="flex" alignItems="center">
            <Button
              variant="outlined"
              fullWidth
              onClick={handleBuscarEndereco}
              disabled={clubeSharedFieldsLocked || buscandoEndereco}
            >
              {buscandoEndereco ? 'Buscando...' : 'Buscar'}
            </Button>
          </Grid>
          {buscandoEndereco ? (
            <Grid item xs={12} display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Buscando endereço...
              </Typography>
            </Grid>
          ) : null}
          {enderecoResultados.length > 0 ? (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
                {enderecoResultados.map((resultado) => (
                  <Button
                    key={resultado.place_id || `${resultado.lat}-${resultado.long}`}
                    onClick={() => handleSelecionarEndereco(resultado)}
                    disabled={clubeSharedFieldsLocked}
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      px: 2,
                      py: 1,
                    }}
                  >
                    {resultado.formatted_address || 'Endereço sem descrição'}
                  </Button>
                ))}
              </Paper>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Latitude"
              fullWidth
              type="number"
              inputProps={{ step: 0.000001 }}
              value={localidadeForm.lat}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('lat', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Longitude"
              fullWidth
              type="number"
              inputProps={{ step: 0.000001 }}
              value={localidadeForm.long}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('long', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Raio (m)"
              fullWidth
              type="number"
              inputProps={{ min: 0 }}
              value={localidadeForm.raio}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('raio', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Endereço completo"
              fullWidth
              value={localidadeForm.formatted_address || ''}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('formatted_address', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Logradouro"
              fullWidth
              value={localidadeForm.logradouro || ''}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('logradouro', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Número"
              fullWidth
              value={localidadeForm.numero || ''}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('numero', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Complemento"
              fullWidth
              value={localidadeForm.complemento || ''}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('complemento', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              label="Bairro"
              fullWidth
              value={localidadeForm.bairro || ''}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('bairro', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Cidade"
              fullWidth
              value={localidadeForm.cidade || ''}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('cidade', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={1}>
            <TextField
              label="UF"
              fullWidth
              value={localidadeForm.estado || ''}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('estado', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              label="CEP"
              fullWidth
              value={localidadeForm.cep || ''}
              disabled={clubeSharedFieldsLocked}
              onChange={(e) => handleLocalidadeFieldChange('cep', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCloseLocalidadeDialog} color="inherit" disabled={savingLocalidade}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveLocalidade}
          disabled={clubeSharedFieldsLocked || savingLocalidade}
          className="clube-primary-button"
        >
          {savingLocalidade ? 'Salvando...' : localidadeForm.id ? 'Atualizar' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      open={snackbar.open}
      autoHideDuration={3000}
      onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
    >
      <Alert severity={snackbar.severity} variant="filled">
        {snackbar.message}
      </Alert>
    </Snackbar>

    </>
  );
}
